/**
 * Where a header popover (the account menu, the "More actions" menu) goes so it stays inside the window.
 *
 * A popover anchored to a small button with `right: 0` and a fixed width runs past the left edge of whatever contains it as
 * soon as the button sits closer to that edge than the popover is wide: in the desktop window's Comfy layout the avatar
 * button is a third of the way across the header, the main surface clips its overflow, and the menu's icons and first
 * letters were cut off (owner screenshot, 2026-10-17). placePopover keeps the popover inside its bounds (the viewport, cut
 * down to the widget's panel when it has one) with POPOVER_GUTTER on every side, shrinks it when the bounds are narrower,
 * and otherwise leaves it where its anchor puts it: its end aligned with the button's, under the button, or above it where
 * there is no room below.
 */
import { useLayoutEffect, useState } from 'react';
/** The space kept between a header popover and the window's (or the panel's) edge, on every side. */
export const POPOVER_GUTTER = 8;
/** The popover's place: inside the bounds with the gutter, no wider than they allow, anchored where there is room. */
export function placePopover({ anchor, bounds, width, height, align, side, gap = 10, gutter = POPOVER_GUTTER }) {
    const minLeft = bounds.left + gutter;
    const maxRight = bounds.right - gutter;
    const minTop = bounds.top + gutter;
    const maxBottom = bounds.bottom - gutter;
    const fitWidth = Math.max(1, Math.min(width, maxRight - minLeft));
    const preferredLeft = align === 'end' ? anchor.right - fitWidth : anchor.left;
    const left = Math.max(minLeft, Math.min(preferredLeft, maxRight - fitWidth));
    const maxHeight = Math.max(1, maxBottom - minTop);
    const fitHeight = Math.min(Math.max(0, height), maxHeight);
    const below = anchor.bottom + gap;
    const above = anchor.top - gap - fitHeight;
    const roomBelow = below + fitHeight <= maxBottom;
    const roomAbove = above >= minTop;
    const preferredTop = side === 'below'
        ? (roomBelow || !roomAbove ? below : above)
        : (roomAbove || !roomBelow ? above : below);
    const top = Math.max(minTop, Math.min(preferredTop, maxBottom - fitHeight));
    return { left, top, width: fitWidth, maxHeight };
}
/**
 * The box a popover may use: the visual viewport, cut down to `host` (the widget's panel) when one is given, so a popover
 * never leaves the panel on a page that embeds it in a corner.
 */
export function popoverBounds(view, host) {
    const viewport = view.visualViewport;
    const left = viewport?.offsetLeft ?? 0;
    const top = viewport?.offsetTop ?? 0;
    const bounds = { left, top, right: left + (viewport?.width ?? view.innerWidth), bottom: top + (viewport?.height ?? view.innerHeight) };
    if (!host)
        return bounds;
    const rect = host.getBoundingClientRect();
    // A host that is not laid out (jsdom, a hidden panel) is no bound at all.
    if (!(rect.width > 0) || !(rect.height > 0))
        return bounds;
    return {
        left: Math.max(bounds.left, rect.left),
        top: Math.max(bounds.top, rect.top),
        right: Math.min(bounds.right, rect.right),
        bottom: Math.min(bounds.bottom, rect.bottom),
    };
}
/**
 * The inline style that puts a popover at `placement` inside `container`, the element it is positioned against (its
 * offsetParent: the portal target when the popover is portaled, else its own positioned wrapper).
 */
export function popoverStyle(placement, container) {
    const rect = container.getBoundingClientRect();
    const clientLeft = 'clientLeft' in container ? container.clientLeft : 0;
    const clientTop = 'clientTop' in container ? container.clientTop : 0;
    return {
        position: 'absolute',
        left: placement.left - rect.left - clientLeft,
        top: placement.top - rect.top - clientTop,
        right: 'auto',
        bottom: 'auto',
        width: placement.width,
        maxHeight: placement.maxHeight,
    };
}
/**
 * The placement of an open popover, measured in a layout effect and again on every resize, scroll or size change of the
 * trigger or the popover; null while closed or before the first measure (nothing is laid out: jsdom, a server render), when
 * the stylesheet's own place applies.
 */
export function usePopoverPlacement(open, trigger, popover, container, options) {
    const [style, setStyle] = useState(null);
    const { align, side, width, gap, host } = options;
    useLayoutEffect(() => {
        if (!open) {
            setStyle(null);
            return undefined;
        }
        const measure = () => {
            const button = trigger.current;
            const popup = popover.current;
            const box = container && 'current' in container ? container.current : container;
            const view = button?.ownerDocument.defaultView;
            if (!button || !popup || !box || !view)
                return;
            const anchor = button.getBoundingClientRect();
            // Nothing laid out (jsdom, a hidden panel): the stylesheet's place stands.
            if (!(anchor.width > 0) && !(anchor.height > 0) && box.getBoundingClientRect().width === 0) {
                setStyle(null);
                return;
            }
            const placement = placePopover({ anchor, bounds: popoverBounds(view, host ?? box.closest('.gyro-panel')), width, height: popup.getBoundingClientRect().height, align, side, gap });
            const next = popoverStyle(placement, box);
            setStyle((previous) => previous && Object.keys(next).every((key) => previous[key] === next[key]) ? previous : next);
        };
        measure();
        const view = trigger.current?.ownerDocument.defaultView;
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : undefined;
        if (trigger.current)
            observer?.observe(trigger.current);
        if (popover.current)
            observer?.observe(popover.current);
        view?.addEventListener('resize', measure);
        view?.addEventListener('scroll', measure, true);
        view?.visualViewport?.addEventListener('resize', measure);
        return () => {
            observer?.disconnect();
            view?.removeEventListener('resize', measure);
            view?.removeEventListener('scroll', measure, true);
            view?.visualViewport?.removeEventListener('resize', measure);
        };
    }, [open, trigger, popover, container, align, side, width, gap, host]);
    return style;
}
//# sourceMappingURL=popoverPlacement.js.map